import React, { useState, useEffect } from 'react';
import { X, Search, Filter, Package, Heart, Users } from 'lucide-react';
import { Button } from '../UI';
import ProductCard from '../Products/ProductCard';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';

const AddProductsToPantryModal = ({ pantry, onClose, onAddProducts }) => {
  const [activeTab, setActiveTab] = useState('catalog');
  const [allProducts, setAllProducts] = useState([]);
  const [savedProducts, setSavedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [applyFilters, setApplyFilters] = useState(true);

  // Get products already in pantry
  const pantryProductIds = new Set(pantry.items?.map(item => item.productId) || []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);

        // Fetch catalog products
        const productsRef = collection(db, 'products');
        const q = query(
          productsRef,
          where('isVerified', '==', true),
          orderBy('createdAt', 'desc'),
          limit(200)
        );
        const snapshot = await getDocs(q);
        const productsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        console.log('Fetched products:', productsData.length);
        setAllProducts(productsData);

        // Fetch saved products
        if (auth.currentUser) {
          const favoritesRef = collection(db, 'userFavoriteProducts');
          const favQuery = query(
            favoritesRef,
            where('userId', '==', auth.currentUser.uid)
          );
          const favSnapshot = await getDocs(favQuery);
          const favoriteProductIds = favSnapshot.docs.map(doc => doc.data().productId);

          if (favoriteProductIds.length > 0) {
            const savedProductsData = productsData.filter(p => favoriteProductIds.includes(p.id));
            setSavedProducts(savedProductsData);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching products:', error);
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const toggleProductSelection = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const handleAddSelected = () => {
    const sourceProducts = activeTab === 'catalog' ? getDisplayedProducts() : savedProducts;
    const productsToAdd = sourceProducts.filter(p => selectedProducts.has(p.id));
    onAddProducts(productsToAdd);
  };

  // Get products to display based on active tab, filters, and search
  const getDisplayedProducts = () => {
    let products = activeTab === 'catalog' ? allProducts : savedProducts;

    // Apply dietary filters if enabled and pantry has care profiles
    if (applyFilters && pantry.careProfileIds?.length > 0 && activeTab === 'catalog') {
      // TODO: Apply dietary filter based on care profiles
      // For now, just return all products
      console.log('Would filter for profiles:', pantry.careProfileIds);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      products = products.filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.brand?.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query)
      );
    }

    return products;
  };

  const displayedProducts = getDisplayedProducts();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-lg">
      <div className="bg-background rounded-large border border-text-tertiary/20 max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-xl border-b border-text-tertiary/10 flex items-center justify-between">
          <div>
            <h2 className="text-title-2 font-rounded text-primary mb-sm">
              Add Products to {pantry.name}
            </h2>
            <div className="flex items-center gap-md">
              <p className="text-caption text-text-secondary">
                {selectedProducts.size} selected
              </p>
              {pantry.careProfileIds?.length > 0 && applyFilters && (
                <div className="flex items-center gap-xs px-sm py-xs bg-success/10 rounded-full border border-success/20">
                  <Users size={14} className="text-success" />
                  <span className="text-caption text-success">
                    Filtering for {pantry.careProfileIds.length} profile{pantry.careProfileIds.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-surface flex items-center justify-center transition-all"
          >
            <X size={24} className="text-text-tertiary" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-xl pt-lg flex gap-sm border-b border-text-tertiary/10">
          <button
            onClick={() => setActiveTab('catalog')}
            className={`px-lg py-md text-callout font-semibold transition-all relative ${
              activeTab === 'catalog'
                ? 'text-primary'
                : 'text-text-secondary hover:text-primary'
            }`}
          >
            <Package size={18} className="inline mr-sm" />
            Product Catalog ({allProducts.length})
            {activeTab === 'catalog' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab('saved')}
            className={`px-lg py-md text-callout font-semibold transition-all relative ${
              activeTab === 'saved'
                ? 'text-primary'
                : 'text-text-secondary hover:text-primary'
            }`}
          >
            <Heart size={18} className="inline mr-sm" />
            Saved Products ({savedProducts.length})
            {activeTab === 'saved' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
            )}
          </button>
        </div>

        {/* Search & Filter Bar */}
        <div className="px-xl py-lg border-b border-text-tertiary/10">
          <div className="flex gap-md">
            <div className="flex-1 relative">
              <Search className="absolute left-md top-1/2 -translate-y-1/2 text-text-tertiary" size={20} />
              <input
                type="text"
                placeholder="Search by name, brand, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-xxxl pr-md py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body"
              />
            </div>
            {pantry.careProfileIds?.length > 0 && (
              <label className="flex items-center gap-sm px-md py-sm rounded-medium border border-text-tertiary/20 cursor-pointer hover:border-primary/30 transition-all">
                <input
                  type="checkbox"
                  checked={applyFilters}
                  onChange={(e) => setApplyFilters(e.target.checked)}
                  className="w-4 h-4 rounded border-text-tertiary/30 text-primary focus:ring-primary"
                />
                <Filter size={18} className="text-primary" />
                <span className="text-callout text-text-primary">Apply Dietary Filters</span>
              </label>
            )}
          </div>
          <div className="flex items-center justify-between mt-sm">
            <p className="text-caption text-text-secondary">
              {displayedProducts.length} product{displayedProducts.length !== 1 ? 's' : ''} found
              {searchQuery && ` for "${searchQuery}"`}
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="text-caption text-primary hover:text-primary/80"
              >
                Clear search
              </button>
            )}
          </div>
        </div>

        {/* Products Grid */}
        <div className="flex-1 overflow-y-auto p-xl">
          {loading ? (
            <div className="text-center py-xxxl">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto mb-md"></div>
              <p className="text-callout text-text-secondary">Loading products...</p>
            </div>
          ) : displayedProducts.length === 0 ? (
            <div className="text-center py-xxxl bg-gradient-to-br from-surface to-primary/5 rounded-large border border-text-tertiary/10">
              <Package size={48} className="text-text-tertiary mx-auto mb-md" />
              <h3 className="text-title-3 text-primary mb-sm">No products found</h3>
              <p className="text-caption text-text-secondary">
                {searchQuery 
                  ? `No products match "${searchQuery}"` 
                  : activeTab === 'saved' 
                    ? 'You haven\'t saved any products yet'
                    : 'No products available'}
              </p>
              {searchQuery && (
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => setSearchQuery('')}
                  className="mt-md"
                >
                  Clear search
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-md">
              {displayedProducts.map(product => {
                const isInPantry = pantryProductIds.has(product.id);
                const isSelected = selectedProducts.has(product.id);
                
                return (
                  <div 
                    key={product.id} 
                    className={`relative ${isInPantry ? 'opacity-50' : ''}`}
                  >
                    {!isInPantry && (
                      <div className="absolute top-2 right-2 z-10">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleProductSelection(product.id);
                          }}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                            isSelected
                              ? 'bg-primary border-primary'
                              : 'bg-surface border-text-tertiary/30 hover:border-primary'
                          }`}
                        >
                          {isSelected && (
                            <X size={14} className="text-white" />
                          )}
                        </button>
                      </div>
                    )}
                    <ProductCard
                      product={product}
                      onClick={() => !isInPantry && toggleProductSelection(product.id)}
                    />
                    {isInPantry && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-large">
                        <span className="text-white text-callout font-semibold">
                          Already in pantry
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-xl border-t border-text-tertiary/10 flex items-center justify-between">
          <p className="text-callout text-text-secondary">
            {selectedProducts.size} product{selectedProducts.size !== 1 ? 's' : ''} selected
          </p>
          <div className="flex gap-md">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleAddSelected}
              disabled={selectedProducts.size === 0}
            >
              Add Selected
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProductsToPantryModal;
